import { createMedia } from "@artsy/fresnel";
import React from "react";
import {
  Button,
  Icon,
  Table,
  Container,
  Modal,
  Image,
} from "semantic-ui-react";
import { Product } from "../../models/product.model";
import { Property } from "../../models/property.model";
import moment from "moment";
import { NavigateFunction } from "react-router-dom";

import { Address } from "../AddressSelector/Address";

import SecurePaymentLogos from "../../resources/images/Secure-pay-logo.jpg";

const AppMedia = createMedia({
  breakpoints: {
    mobile: 0,
    desktop: 769,
  },
});
export const mediaStyle = AppMedia.createMediaStyle();
const { Media, MediaContextProvider } = AppMedia;

interface IProps {
  startDate: string;
  properties: Array<Property>;

  onAddProperty(): void;

  onRemoveFromBasket(property: Property, product: Product): void;

  onPropertySelect(index: number, address: Address): void;

  onCheckout(): void;

  readOnly?: boolean;

  onExpand?(isMaximized: boolean): void;

  navigate: NavigateFunction;
}

interface IState {
  showConfirmationModal: boolean;
  confirmationAgreement: boolean;
  openSummary: boolean;
}

export class OrderSummary extends React.Component<IProps, IState> {
  state: IState = {
    showConfirmationModal: false,
    confirmationAgreement: false,
    openSummary: false,
  };

  getSnapshotBeforeUpdate(prevProps: IProps, prevState: IState) {
    return null;
  }

  componentDidUpdate(prevProps: IProps, prevState: IState, snapshot: any) {}

  renderPropertyTotal = (property: Property) => {
    let unitsCount =
      property.tenancyCount > 1
        ? property.tenancyCount
        : property.numberOfFlats;
    let propertyTotal = 0;

    property.products.forEach((product) => {
      propertyTotal = propertyTotal + product.total * unitsCount;
    });

    return propertyTotal.toFixed(2);
  };

  renderOrderSummary = (properties: Array<Property>) => {
    return (
      <Table basic="very" className="orderSummaryProducts">
        <Table.Body>
          {properties.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan="4">No products selected</Table.Cell>
            </Table.Row>
          )}
          {properties.map((property: Property, idx) => (
            <React.Fragment key={idx}>
              <Table.Row>
                <Table.Cell className="orderSummary-propertyNumber">
                  {1 + idx}
                </Table.Cell>
                <Table.Cell
                  colSpan="3"
                  className="orderSummary-propertyAddress"
                >
                  {this.props.readOnly ? (
                    `${property.address.address1}, ${property.address.postCode}`
                  ) : (
                    <a
                      href="#"
                      onClick={async (event: any) => {
                        this.props.onPropertySelect(idx, property.address);
                      }}
                    >{`${property.address.address1}, ${property.address.postCode}`}</a>
                  )}
                </Table.Cell>
              </Table.Row>

              {property.products.map((product: Product, prodIdx) => (
                <Table.Row key={prodIdx} className="productDetails">
                  <Table.Cell className="productEmptyCell" />
                  <Table.Cell className="productName" verticalAlign="top">
                    {(property.tenancyCount > 1
                      ? property.tenancyCount
                      : property.numberOfFlats) > 1
                      ? (property.tenancyCount > 1
                          ? property.tenancyCount
                          : property.numberOfFlats) + " x "
                      : ""}
                    {product.productDisplayName}
                    <span style={{ whiteSpace: "nowrap" }}>
                      <br />({product.coverDurationMonths} months)
                    </span>
                  </Table.Cell>
                  <Table.Cell
                    className="productPrice"
                    textAlign="right"
                    verticalAlign="top"
                  >
                    &pound;{product.total.toFixed(2)}{" "}
                  </Table.Cell>
                  {this.props.readOnly ? null : (
                    <Table.Cell
                      className="productRemove"
                      textAlign="right"
                      verticalAlign="top"
                    >
                      <Icon
                        name="minus circle"
                        className="removeProduct"
                        onClick={async (event: any) => {
                          document.body.style.overflow = "";
                          await this.props.onRemoveFromBasket(
                            property,
                            product,
                          );
                        }}
                      />
                    </Table.Cell>
                  )}
                </Table.Row>
              ))}
            </React.Fragment>
          ))}
        </Table.Body>
      </Table>
    );
  };

  renderOrderTotal = (properties: Array<Property>) => {
    let orderTotal = 0;
    properties.forEach((property) => {
      let unitsCount =
        property.tenancyCount > 1
          ? property.tenancyCount
          : property.numberOfFlats;
      let propertyTotal = 0;

      property.products.forEach((product) => {
        propertyTotal = propertyTotal + product.total * unitsCount;
      });

      orderTotal = orderTotal + propertyTotal;
    });

    return orderTotal.toFixed(2);
  };

  renderConfirmationModal = () => {
    return (
      <Modal
        size="tiny"
        open={this.state.showConfirmationModal}
        onClose={async () => {
          await this.setState({ showConfirmationModal: false });
        }}
      >
        <Modal.Content>
          <div
            style={{ cursor: "pointer" }}
            onClick={async () => {
              this.setState({
                confirmationAgreement: !this.state.confirmationAgreement,
              });
            }}
          >
            Before proceeding, please confirm the following:
            <br />
            <ul>
              <li>
                I either live in the UK or I live in the EU, EEA, Australia,
                Canada, the Channel Islands, the Isle of Man, Japan, New
                Zealand, Singapore, Switzerland or the USA and have a UK-based
                property managing agent and a UK bank account;
              </li>
              <li>
                There have been no arrears on the property in the past 12
                months;
              </li>
              <li>
                There are no known incidents that could give rise to a claim
                under this policy and no section notices have been issued in the
                last 3 months;
              </li>
              <li>
                Where the cover includes Rent Protection, that the tenants have
                gross income of 2.5 times the monthly rent or have a guarantor
                in place with income 3 times the monthly rent;
              </li>
              <li>
                Where the cover includes Rent Protection, that the tenants have
                no CCJs, bankruptcies, IVAs settled or outstanding in the last 3
                years;
              </li>
              <li>
                The tenants meet the right to rent requirements or have two
                valid forms of ID;
              </li>
              <li>
                I have a tenancy agreement directly with the tenant (e.g. it is
                not rented through a council or other third party scheme);
              </li>
              <li>
                I have read and understand the relevant Product Information and
                Policy Wording;
              </li>
              <li>
                I have read and understand my{" "}
                <a
                  href="https://www.alanboswell.com/wp-content/uploads/Landlord-T-Cs.pdf"
                  target="_blank"
                  rel="noreferrer"
                >
                  obligations
                </a>{" "}
                applying to this purchase and understand the&nbsp;
                <a
                  href="https://www.alanboswell.com/wp-content/uploads/ABG-Client-Terms-of-Business-GI.pdf"
                  target="_blank"
                  rel="noreferrer"
                >
                  terms of business
                </a>
                ;
              </li>
              <li>I understand this is a non-advised sale.</li>
            </ul>
            <input
              type="checkbox"
              checked={this.state.confirmationAgreement}
              onChange={async () => {
                this.setState({
                  confirmationAgreement: !this.state.confirmationAgreement,
                });
              }}
            />{" "}
            I confirm all the above.
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={async () => {
              document.body.style.overflow = "inherit";
              await this.setState({
                showConfirmationModal: false,
                confirmationAgreement: false,
              });
            }}
          >
            Cancel
          </Button>
          <Button
            positive
            disabled={!this.state.confirmationAgreement}
            onClick={async () => {
              document.body.style.overflow = "inherit";

              await this.props.onCheckout();
            }}
          >
            Continue
          </Button>
        </Modal.Actions>
      </Modal>
    );
  };

  render() {
    return (
      <React.Fragment>
        <Container className={"orderSummaryWrapper"}>
          <MediaContextProvider>
            <Media greaterThanOrEqual="desktop">
              <Table basic="very" className="orderSummaryHeader desktop">
                <Table.Body>
                  <Table.Row className="clearfix orderSummaryHeaderRow">
                    <Table.Cell className="orderSummaryTitle">
                      <span className="h2">Order Summary</span>&nbsp;
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row className="clearfix orderSummaryHeaderRow">
                    <Table.Cell textAlign="left" className="orderSummaryDate">
                      Start date
                    </Table.Cell>
                    <Table.Cell textAlign="right" className="orderSummaryDate">
                      {moment(this.props.startDate).format("DD/MM/YYYY")}
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </Media>
            <Media at="mobile">
              <Table
                basic="very"
                attached
                className="orderSummaryHeader mobile"
              >
                <Table.Body>
                  <Table.Row className="clearfix orderSummaryHeaderRow">
                    <Table.Cell className="orderSummaryTitle">
                      <span className="h2">Order Summary</span>&nbsp;
                      <span className="orderSummaryTitleTotal">
                        Total £{this.renderOrderTotal(this.props.properties)}
                      </span>
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
              <Table basic="very" attached className="orderSummaryStartDate">
                <Table.Body>
                  <Table.Row className="clearfix orderSummaryHeaderRow">
                    <Table.Cell textAlign="left" className="orderSummaryDate">
                      <b>Start date:&nbsp;</b>
                    </Table.Cell>
                    <Table.Cell textAlign="left" className="orderSummaryDate">
                      {moment(this.props.startDate).format("DD/MM/YYYY")}
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </Media>
            {this.renderOrderSummary(this.props.properties)}
            <Table basic="very" attached className="orderSummaryTotal">
              <Table.Body>
                <Table.Row>
                  <Table.Cell textAlign="right">
                    Total £{this.renderOrderTotal(this.props.properties)}
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
            <Media at="mobile">
              {this.props.readOnly ? null : (
                <>
                  <Table
                    basic="very"
                    attached
                    className="orderSummaryButtons no-margin small-devices"
                  >
                    <Table.Body>
                      <Table.Row className="orderSummaryButtonsMobile clearfix">
                        <Table.Cell className="orderSummaryButtonsWrapper add">
                          <Button
                            className="secondaryButton addAnotherPropertyButton"
                            size="large"
                            disabled={this.props.properties.length === 0}
                            onClick={(event: any) => {
                              event.preventDefault();
                              this.props.onAddProperty();
                              event.stopPropagation();
                            }}
                          >
                            Add another property
                          </Button>
                        </Table.Cell>
                        <Table.Cell></Table.Cell>
                        <Table.Cell className="orderSummaryButtonsWrapper checkout">
                          <Button
                            size="large"
                            className="fullwidth checkoutButton"
                            positive={this.props.properties.length !== 0}
                            disabled={this.props.properties.length === 0}
                            onClick={(event: any) => {
                              event.preventDefault();
                              document.body.style.overflow = "inherit";
                              this.setState({ showConfirmationModal: true });
                              event.stopPropagation();
                            }}
                          >
                            Checkout
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    </Table.Body>
                  </Table>
                </>
              )}
            </Media>
            <Media greaterThanOrEqual="desktop">
              {this.props.readOnly ? null : (
                <>
                  <Table basic="very" attached className="orderSummaryButtons">
                    <Table.Body>
                      <Table.Row>
                        <Table.Cell className="orderSummaryButtonsWrapper">
                          <Button
                            className="secondaryButton addAnotherPropertyButton"
                            size="large"
                            disabled={this.props.properties.length === 0}
                            onClick={(event: any) => {
                              event.preventDefault();
                              this.props.onAddProperty();
                            }}
                          >
                            Add another property
                          </Button>
                          <Button
                            size="large"
                            className="fullwidth checkoutButton"
                            positive={this.props.properties.length !== 0}
                            disabled={this.props.properties.length === 0}
                            onClick={(event: any) => {
                              event.preventDefault();
                              document.body.style.overflow = "inherit";
                              this.setState({ showConfirmationModal: true });
                            }}
                          >
                            Checkout
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    </Table.Body>
                  </Table>
                </>
              )}
            </Media>
          </MediaContextProvider>
        </Container>
        {this.state.showConfirmationModal
          ? this.renderConfirmationModal()
          : null}
      </React.Fragment>
    );
  }
}

export default OrderSummary;
